import { supabase } from '@/lib/supabase';
import { v4 as uuidv4 } from 'uuid';

// Enhanced tracking configuration
interface TrackingConfig {
  metaPixelId?: string;
  metaEvents?: Record<string, any>;
  gaMeasurementId?: string;
  gaEvents?: Record<string, any>;
}

// Event data interfaces
interface ViewEventData {
  itemId: string;
  itemType: 'property' | 'car' | 'product';
  itemTitle: string;
  itemPrice: number;
  userId: string;
  source?: string;
}

interface LeadEventData {
  itemId: string;
  itemType: 'property' | 'car' | 'product';
  itemTitle: string;
  itemPrice: number;
  userId: string;
  leadName: string;
  leadEmail: string;
  leadPhone?: string;
  source?: string;
}

interface ConversionEventData {
  itemId: string;
  itemType: 'property' | 'car' | 'product';
  value: number;
  currency: string;
  userId: string;
}

// Load tracking settings for a user
export const loadTrackingSettings = async (userId: string): Promise<TrackingConfig | null> => {
  try {
    const { data, error } = await supabase
      .from('tracking_settings')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .maybeSingle();

    if (error) {
      console.error('Error loading tracking settings:', error);
      return null;
    }

    if (!data) return null;

    return {
      metaPixelId: data.meta_pixel_id,
      metaEvents: data.meta_events,
      gaMeasurementId: data.ga_measurement_id,
      gaEvents: data.ga_events,
    };
  } catch (error) {
    console.error('Error in loadTrackingSettings:', error);
    return null;
  }
};

// Initialize Meta Pixel
export const injectMetaPixel = (pixelId: string) => {
  if (!pixelId || typeof window === 'undefined') return;

  // Check if already loaded
  if (window.fbq) return;

  // Add Meta Pixel base code
  const script = document.createElement('script');
  script.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${pixelId}');
    fbq('track', 'PageView');
  `;
  document.head.appendChild(script);

  // Add Meta Pixel noscript fallback
  const noscript = document.createElement('noscript');
  noscript.innerHTML = `
    <img height="1" width="1" style="display:none"
    src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1"/>
  `;
  document.body.appendChild(noscript);
};

// Initialize Google Analytics
export const injectGoogleAnalytics = (measurementId: string) => {
  if (!measurementId || typeof window === 'undefined') return;

  // Check if already loaded
  if (window.gtag) return;

  // Add Google Analytics base code
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  // Add Google Analytics configuration
  const configScript = document.createElement('script');
  configScript.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${measurementId}', {
      page_title: document.title,
      page_location: window.location.href
    });
  `;
  document.head.appendChild(configScript);
};

// Enhanced Meta Pixel event tracking
export const trackMetaEvent = (eventName: string, data?: any, customEvents?: Record<string, any>) => {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;

  try {
    // Track standard event
    if (data) {
      window.fbq('track', eventName, data);
    } else {
      window.fbq('track', eventName);
    }

    // Track custom events if configured
    if (customEvents && customEvents[eventName]) {
      window.fbq('trackCustom', eventName, {
        ...data,
        ...customEvents[eventName]
      });
    }
  } catch (error) {
    console.error('Error tracking Meta event:', error);
  }
};

// Enhanced Google Analytics event tracking
export const trackGoogleEvent = (eventName: string, data?: any, customEvents?: Record<string, any>) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

  try {
    // Track standard event
    window.gtag('event', eventName, {
      event_category: 'engagement',
      event_label: data?.content_name || data?.item_name,
      value: data?.value,
      ...data
    });

    // Track custom events if configured
    if (customEvents && customEvents[eventName]) {
      window.gtag('event', `custom_${eventName}`, {
        ...data,
        ...customEvents[eventName]
      });
    }
  } catch (error) {
    console.error('Error tracking Google event:', error);
  }
};

// Enhanced view tracking with better analytics integration
export const trackView = async (itemId: string, type: 'property' | 'car' | 'product' = 'property', additionalData?: Partial<ViewEventData>) => {
  try {
    const viewerId = localStorage.getItem('viewer_id') || uuidv4();
    localStorage.setItem('viewer_id', viewerId);

    // Track in database
    const { error } = await supabase
      .from('property_views')
      .upsert(
        {
          property_id: itemId,
          viewer_id: viewerId,
          listing_type: type,
          source: additionalData?.source || document.referrer || 'direct',
          view_date: new Date().toISOString().split('T')[0],
          is_unique: true
        },
        {
          onConflict: 'property_id,viewer_id,view_date,listing_type',
          ignoreDuplicates: true
        }
      );

    if (error) throw error;

    // Enhanced analytics tracking
    if (additionalData) {
      const eventData = {
        content_type: type,
        content_ids: [itemId],
        content_name: additionalData.itemTitle,
        value: additionalData.itemPrice,
        currency: 'BRL'
      };

      // Load user's tracking settings
      if (additionalData.userId) {
        const trackingConfig = await loadTrackingSettings(additionalData.userId);
        
        if (trackingConfig) {
          // Track Meta Pixel events
          if (trackingConfig.metaPixelId) {
            trackMetaEvent('ViewContent', eventData, trackingConfig.metaEvents);
          }

          // Track Google Analytics events
          if (trackingConfig.gaMeasurementId) {
            trackGoogleEvent('view_item', {
              item_id: itemId,
              item_name: additionalData.itemTitle,
              item_category: type,
              value: additionalData.itemPrice,
              currency: 'BRL'
            }, trackingConfig.gaEvents);
          }
        }
      }
    }

    return true;
  } catch (err) {
    console.error('Error tracking view:', err);
    return false;
  }
};

// Enhanced lead tracking with conversion events
export const trackLead = async (data: {
  itemId: string;
  itemType: 'property' | 'car' | 'product';
  name: string;
  email: string;
  phone?: string;
  message?: string;
  source?: string;
  userId?: string;
  itemTitle?: string;
  itemPrice?: number;
}) => {
  try {
    // Track in database
    const { error } = await supabase
      .from('leads')
      .insert({
        property_id: data.itemId,
        listing_type: data.itemType,
        name: data.name,
        email: data.email,
        phone: data.phone,
        message: data.message,
        source: data.source || 'form',
        status: 'new'
      });

    if (error) throw error;

    // Enhanced analytics tracking
    if (data.userId && data.itemTitle && data.itemPrice) {
      const trackingConfig = await loadTrackingSettings(data.userId);
      
      if (trackingConfig) {
        const eventData = {
          content_type: data.itemType,
          content_ids: [data.itemId],
          content_name: data.itemTitle,
          value: data.itemPrice,
          currency: 'BRL'
        };

        // Track Meta Pixel lead event
        if (trackingConfig.metaPixelId) {
          trackMetaEvent('Lead', eventData, trackingConfig.metaEvents);
        }

        // Track Google Analytics lead event
        if (trackingConfig.gaMeasurementId) {
          trackGoogleEvent('generate_lead', {
            item_id: data.itemId,
            item_name: data.itemTitle,
            item_category: data.itemType,
            value: data.itemPrice,
            currency: 'BRL'
          }, trackingConfig.gaEvents);
        }
      }
    }

    return true;
  } catch (err) {
    console.error('Error tracking lead:', err);
    return false;
  }
};

// Track conversion events (when a lead converts to a sale)
export const trackConversion = async (data: ConversionEventData) => {
  try {
    const trackingConfig = await loadTrackingSettings(data.userId);
    
    if (trackingConfig) {
      const eventData = {
        content_type: data.itemType,
        content_ids: [data.itemId],
        value: data.value,
        currency: data.currency
      };

      // Track Meta Pixel purchase event
      if (trackingConfig.metaPixelId) {
        trackMetaEvent('Purchase', eventData, trackingConfig.metaEvents);
      }

      // Track Google Analytics purchase event
      if (trackingConfig.gaMeasurementId) {
        trackGoogleEvent('purchase', {
          transaction_id: data.itemId,
          value: data.value,
          currency: data.currency,
          items: [{
            item_id: data.itemId,
            item_name: `${data.itemType}_${data.itemId}`,
            item_category: data.itemType,
            quantity: 1,
            price: data.value
          }]
        }, trackingConfig.gaEvents);
      }
    }

    return true;
  } catch (error) {
    console.error('Error tracking conversion:', error);
    return false;
  }
};

// Enhanced stats with conversion funnel analysis
export const getStats = async (itemId: string, type: 'property' | 'car' | 'product') => {
  try {
    const [viewsResponse, leadsResponse] = await Promise.all([
      supabase
        .from('property_views')
        .select('*', { count: 'exact' })
        .eq('property_id', itemId)
        .eq('listing_type', type),
      supabase
        .from('leads')
        .select('*', { count: 'exact' })
        .eq('property_id', itemId)
        .eq('listing_type', type)
    ]);

    const views = viewsResponse.count || 0;
    const leads = leadsResponse.count || 0;
    const conversionRate = views ? (leads / views) * 100 : 0;

    return {
      views,
      leads,
      conversionRate: Math.round(conversionRate * 100) / 100
    };
  } catch (error) {
    console.error('Error getting stats:', error);
    return { views: 0, leads: 0, conversionRate: 0 };
  }
};

// Get detailed analytics for a user
export const getUserAnalytics = async (userId: string, dateRange?: { start: Date; end: Date }) => {
  try {
    const startDate = dateRange?.start || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000); // 30 days ago
    const endDate = dateRange?.end || new Date();

    // Get user's items based on niche type
    const { data: user } = await supabase
      .from('users')
      .select('niche_type')
      .eq('id', userId)
      .single();

    if (!user) throw new Error('User not found');

    let itemsQuery;
    let listingType;

    switch (user.niche_type) {
      case 'imoveis':
        itemsQuery = supabase.from('properties').select('id').eq('user_id', userId);
        listingType = 'property';
        break;
      case 'veiculos':
        itemsQuery = supabase.from('cars').select('id').eq('user_id', userId);
        listingType = 'car';
        break;
      case 'diversos':
        itemsQuery = supabase.from('products').select('id').eq('user_id', userId);
        listingType = 'product';
        break;
      default:
        throw new Error('Invalid niche type');
    }

    const { data: items } = await itemsQuery;
    const itemIds = items?.map(item => item.id) || [];

    if (itemIds.length === 0) {
      return {
        totalViews: 0,
        totalLeads: 0,
        conversionRate: 0,
        dailyStats: [],
        topPerformingItems: [],
        leadSources: []
      };
    }

    // Get views and leads for the date range
    const [viewsData, leadsData] = await Promise.all([
      supabase
        .from('property_views')
        .select('viewed_at, property_id')
        .in('property_id', itemIds)
        .eq('listing_type', listingType)
        .gte('viewed_at', startDate.toISOString())
        .lte('viewed_at', endDate.toISOString()),
      supabase
        .from('leads')
        .select('created_at, property_id, source')
        .in('property_id', itemIds)
        .eq('listing_type', listingType)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
    ]);

    const views = viewsData.data || [];
    const leads = leadsData.data || [];

    // Calculate daily stats
    const dailyStats = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      const dateStr = currentDate.toISOString().split('T')[0];
      const dayViews = views.filter(v => v.viewed_at.startsWith(dateStr)).length;
      const dayLeads = leads.filter(l => l.created_at.startsWith(dateStr)).length;
      
      dailyStats.push({
        date: dateStr,
        views: dayViews,
        leads: dayLeads,
        conversionRate: dayViews ? (dayLeads / dayViews) * 100 : 0
      });
      
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Get top performing items
    const itemStats = itemIds.map(itemId => {
      const itemViews = views.filter(v => v.property_id === itemId).length;
      const itemLeads = leads.filter(l => l.property_id === itemId).length;
      return {
        itemId,
        views: itemViews,
        leads: itemLeads,
        conversionRate: itemViews ? (itemLeads / itemViews) * 100 : 0
      };
    }).sort((a, b) => b.conversionRate - a.conversionRate).slice(0, 5);

    // Get lead sources
    const leadSources = leads.reduce((acc, lead) => {
      const source = lead.source || 'unknown';
      acc[source] = (acc[source] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      totalViews: views.length,
      totalLeads: leads.length,
      conversionRate: views.length ? (leads.length / views.length) * 100 : 0,
      dailyStats,
      topPerformingItems: itemStats,
      leadSources: Object.entries(leadSources).map(([source, count]) => ({ source, count }))
    };
  } catch (error) {
    console.error('Error getting user analytics:', error);
    throw error;
  }
};

// Initialize tracking for a page
export const initializeTracking = async (userId: string) => {
  try {
    const trackingConfig = await loadTrackingSettings(userId);
    
    if (trackingConfig) {
      if (trackingConfig.metaPixelId) {
        injectMetaPixel(trackingConfig.metaPixelId);
      }
      
      if (trackingConfig.gaMeasurementId) {
        injectGoogleAnalytics(trackingConfig.gaMeasurementId);
      }
    }
  } catch (error) {
    console.error('Error initializing tracking:', error);
  }
};

// Track page view with enhanced data
export const trackPageView = async (userId: string, pageData?: {
  page_title?: string;
  page_location?: string;
  content_group1?: string;
}) => {
  try {
    const trackingConfig = await loadTrackingSettings(userId);
    
    if (trackingConfig) {
      // Track Meta Pixel page view
      if (trackingConfig.metaPixelId) {
        trackMetaEvent('PageView', pageData, trackingConfig.metaEvents);
      }

      // Track Google Analytics page view
      if (trackingConfig.gaMeasurementId) {
        trackGoogleEvent('page_view', {
          page_title: pageData?.page_title || document.title,
          page_location: pageData?.page_location || window.location.href,
          content_group1: pageData?.content_group1
        }, trackingConfig.gaEvents);
      }
    }
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};

// Export types for use in other files
export type { TrackingConfig, ViewEventData, LeadEventData, ConversionEventData };